<template>
  <section id="home" class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-[4.5rem] overflow-visible">
    <div class="relative xl:grid xl:grid-cols-[minmax(0,560px)_minmax(440px,1fr)] 2xl:grid-cols-[minmax(0,600px)_minmax(540px,1fr)] xl:items-center xl:gap-10 2xl:gap-12">
      <div class="relative z-10 max-w-[660px]">
        <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 dark:bg-gray-900/70 border border-blue-200 dark:border-blue-800 mb-6 shadow-sm">
          <span class="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" aria-hidden="true"></span>
          <p class="text-sm sm:text-base font-bold text-blue-700 dark:text-blue-300">
            Websites für Unternehmen
          </p>
        </div>

        <h1 class="text-4xl sm:text-5xl lg:text-[3.35rem] 2xl:text-[4rem] font-extrabold leading-[1.06] tracking-tight text-gray-900 dark:text-gray-100 mb-6">
          Professionelle Webentwicklung aus
          <span class="relative inline-block whitespace-nowrap text-blue-600 dark:text-blue-400">
            München.
            <span class="absolute bottom-2 left-0 w-full h-3 -z-10 rounded-sm bg-blue-200/40 dark:bg-blue-400/10"></span>
          </span>
        </h1>

        <p class="text-lg sm:text-xl xl:text-2xl leading-relaxed max-w-2xl text-gray-600 dark:text-gray-300 mb-8">
          Moderne Websites für Unternehmen, Vereine und Selbstständige – klar gestaltet, mobil optimiert und technisch sauber umgesetzt.
        </p>

        <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 no-select">
          <router-link
            to="/contact"
            class="inline-flex items-center justify-center px-7 py-4 text-base font-bold rounded-2xl shadow-lg transition-all duration-300 bg-blue-600 dark:bg-blue-500 text-white hover:scale-[1.03] focus:outline-none focus:ring-4 focus:ring-blue-200 dark:focus:ring-blue-800"
          >
            Kostenloses Erstgespräch anfragen
            <ArrowRightIcon class="w-5 h-5 ml-2" aria-hidden="true" />
          </router-link>
          <router-link
            to="/portfolio"
            class="inline-flex items-center justify-center px-7 py-4 text-base font-bold rounded-2xl border-2 transition-all duration-300 bg-white/80 dark:bg-gray-800/60 text-gray-800 dark:text-gray-100 border-blue-200 dark:border-gray-700 hover:border-blue-400 hover:scale-[1.03] focus:outline-none focus:ring-4 focus:ring-blue-100 dark:focus:ring-gray-700"
          >
            Projekte ansehen
          </router-link>
        </div>

        <div class="mt-7 grid grid-cols-3 gap-2 text-[0.72rem] sm:gap-2.5 sm:text-sm lg:text-[0.95rem]">
          <span
            v-for="point in keyPoints"
            :key="point"
            class="inline-flex min-w-0 items-center justify-center rounded-full border border-blue-200 dark:border-blue-800 bg-white/75 dark:bg-gray-900/70 px-2 py-2 font-semibold text-gray-700 dark:text-gray-200 shadow-sm sm:px-3"
          >
            <CheckCircleIcon class="mr-1.5 h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400 sm:mr-2 sm:h-4 sm:w-4" aria-hidden="true" />
            <span class="truncate">{{ point }}</span>
          </span>
        </div>
      </div>

      <div class="relative hidden xl:block min-w-0">
        <div class="hero-perspective relative h-[440px]">
          <div class="hero-screen absolute right-2 2xl:right-6 top-8 w-[94%] 2xl:w-[90%] overflow-hidden rounded-[1.7rem] border border-white/90 bg-white p-2.5 shadow-[0_35px_90px_rgba(37,99,235,0.22)] dark:shadow-[0_35px_90px_rgba(0,0,0,0.42)]">
            <div class="flex items-center gap-1.5 px-3 py-2">
              <span class="h-2.5 w-2.5 rounded-full bg-red-400"></span>
              <span class="h-2.5 w-2.5 rounded-full bg-yellow-400"></span>
              <span class="h-2.5 w-2.5 rounded-full bg-green-400"></span>
            </div>
            <img
              :src="heroWebsiteImage.url"
              alt=""
              class="hero-screen-image aspect-[16/10] w-full rounded-[1.15rem] object-cover object-top"
              loading="eager"
              fetchpriority="high"
            />
          </div>
          <div
            v-if="heroWebsiteCaption"
            class="absolute right-2 2xl:right-6 bottom-[-1.75rem] w-[94%] 2xl:w-[90%] text-center"
          >
            <router-link
              v-if="heroWebsiteLinkUrl && !isExternalHeroWebsiteLink"
              :to="heroWebsiteLinkUrl"
              class="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-white/85 px-4 py-2 text-sm font-bold text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-800 dark:border-blue-800 dark:bg-gray-900/80 dark:text-blue-300 dark:hover:border-blue-700 dark:hover:text-blue-200"
            >
              <span><span class="text-gray-500 dark:text-gray-400">Projekt:</span> {{ heroWebsiteCaption }}</span>
              <ArrowRightIcon class="h-4 w-4" aria-hidden="true" />
            </router-link>
            <a
              v-else-if="heroWebsiteLinkUrl"
              :href="heroWebsiteLinkUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center justify-center gap-2 rounded-full border border-blue-200 bg-white/85 px-4 py-2 text-sm font-bold text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:text-blue-800 dark:border-blue-800 dark:bg-gray-900/80 dark:text-blue-300 dark:hover:border-blue-700 dark:hover:text-blue-200"
            >
              <span><span class="text-gray-500 dark:text-gray-400">Projekt:</span> {{ heroWebsiteCaption }}</span>
              <ArrowRightIcon class="h-4 w-4" aria-hidden="true" />
            </a>
            <p
              v-else
              class="inline-flex items-center justify-center rounded-full border border-blue-200 bg-white/85 px-4 py-2 text-sm font-bold text-blue-700 shadow-sm dark:border-blue-800 dark:bg-gray-900/80 dark:text-blue-300"
            >
              <span class="text-gray-500 dark:text-gray-400">Projekt:</span> {{ heroWebsiteCaption }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/vue/24/outline'
import { getPublicImagesByCategory, type SiteImage } from '../../firebase/images'

const keyPoints = [
  'Modernes Design',
  'Klare Struktur',
  'Mobil optimiert'
]

const fallbackHeroImage: SiteImage = {
  id: 'fallback-m-drei',
  url: '/projects/m-drei/WebsiteHero.webp',
  category: 'hero-website',
  name: 'm-drei',
  displayTitle: 'M-Drei Möbelfertigteile',
  linkUrl: '/projects/m-drei',
  uploadedAt: new Date(0),
  storagePath: ''
}

const heroWebsiteImage = ref<SiteImage>(fallbackHeroImage)
const heroWebsiteCaption = computed(() => heroWebsiteImage.value.displayTitle || heroWebsiteImage.value.name)
const heroWebsiteLinkUrl = computed(() => heroWebsiteImage.value.linkUrl?.trim() || '')
const isExternalHeroWebsiteLink = computed(() => /^https?:\/\//i.test(heroWebsiteLinkUrl.value))

const pickRandomImage = (images: SiteImage[]) => {
  return images[Math.floor(Math.random() * images.length)]
}

onMounted(async () => {
  try {
    const heroImages = await getPublicImagesByCategory('hero-website')
    if (heroImages.length > 0) {
      heroWebsiteImage.value = pickRandomImage(heroImages)
    }
  } catch {
    heroWebsiteImage.value = fallbackHeroImage
  }
})
</script>

<style scoped>
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

.hero-perspective {
  perspective: 1200px;
}

.hero-screen {
  transform: translate3d(0, -12px, 0) rotateY(-14deg) rotateX(4deg) rotateZ(1.5deg);
  transform-origin: center left;
  transform-style: preserve-3d;
  transition: transform 450ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 450ms ease;
}

.hero-screen::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  transform: translateX(-120%) skewX(-18deg);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.34), transparent);
  transition: transform 700ms cubic-bezier(0.16, 1, 0.3, 1);
}

.hero-screen:hover {
  transform: translate3d(0, -24px, 0) rotateY(-8deg) rotateX(2deg) rotateZ(0.5deg);
  box-shadow: 0 42px 110px rgba(37, 99, 235, 0.28);
}

.dark .hero-screen:hover {
  box-shadow: 0 42px 110px rgba(0, 0, 0, 0.52);
}

.hero-screen:hover::after {
  transform: translateX(120%) skewX(-18deg);
}

</style>
